import React, { useState, useRef, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Shield, LogOut, LayoutDashboard, User as UserIcon, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';

export default function PublicNav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const linkClass = ({ isActive }) =>
    `text-[11px] font-display tracking-[0.18em] transition-colors ${isActive ? 'text-[#D4A843]' : 'text-gray-300 hover:text-[#00E5FF]'}`;

  // Close account menu on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const onLogout = async () => {
    setMenuOpen(false);
    await logout();
    navigate('/');
  };

  const initials = (user?.name || user?.email || '?')
    .split(/[@\s]/)[0]
    .split(/[.\s]/)
    .map(w => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <nav className="sticky top-0 z-40 bg-[#050507]/85 backdrop-blur-md border-b border-white/5">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="public-nav-logo">
          <div className="w-8 h-8 bg-[#D4A843] rounded-lg flex items-center justify-center shadow-[0_0_18px_rgba(212,168,67,0.45)] group-hover:shadow-[0_0_22px_rgba(0,229,255,0.45)] transition-shadow">
            <Shield className="w-4 h-4 text-[#0A0A0A]" />
          </div>
          <span className="text-white font-display tracking-[0.22em] text-sm">TECHOPS</span>
        </Link>

        <div className="hidden md:flex items-center gap-7">
          <NavLink to="/diagnostics" className={linkClass}>Health Check</NavLink>
          <NavLink to="/marketplace" className={linkClass}>Marketplace</NavLink>
          <NavLink to="/pricing" className={linkClass}>Pricing</NavLink>
          <NavLink to="/blog" className={linkClass}>Blog</NavLink>
          <NavLink to="/about" className={linkClass}>About</NavLink>
          <NavLink to="/contact" className={linkClass}>Contact</NavLink>
        </div>

        <div className="flex items-center gap-2">
          {user ? (
            <>
              <Link to="/dashboard" className="hidden sm:block">
                <Button variant="ghost" className="text-xs text-gray-300 hover:text-white rounded-lg" data-testid="nav-dashboard">
                  <LayoutDashboard className="w-3.5 h-3.5 mr-1.5" /> Dashboard
                </Button>
              </Link>
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setMenuOpen(o => !o)}
                  className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg hover:bg-white/5 transition-colors"
                  data-testid="nav-account-menu"
                >
                  <span className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D4A843] to-[#B8923A] text-[#0A0A0A] text-xs font-bold flex items-center justify-center">{initials}</span>
                  <ChevronDown className="w-3 h-3 text-gray-400" />
                </button>
                {menuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-[#111] border border-gray-800 rounded-lg shadow-xl overflow-hidden" data-testid="nav-account-dropdown">
                    <div className="p-3 border-b border-gray-800">
                      <p className="text-sm font-semibold text-white truncate">{user.name || user.email}</p>
                      {user.name && <p className="text-[11px] text-gray-500 truncate">{user.email}</p>}
                      <p className="text-[10px] text-[#D4A843] uppercase tracking-wide mt-1">{(user.role || '').replace(/_/g, ' ')}</p>
                    </div>
                    <Link to="/dashboard" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 text-xs text-gray-300 hover:bg-white/5 hover:text-white transition-colors" data-testid="nav-menu-dashboard">
                      <LayoutDashboard className="w-3.5 h-3.5" /> Dashboard
                    </Link>
                    <Link to="/profile" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 text-xs text-gray-300 hover:bg-white/5 hover:text-white transition-colors">
                      <UserIcon className="w-3.5 h-3.5" /> Profile
                    </Link>
                    <button
                      onClick={onLogout}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors border-t border-gray-800"
                      data-testid="nav-logout"
                    >
                      <LogOut className="w-3.5 h-3.5" /> Sign out
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost" className="text-xs text-gray-300 hover:text-white rounded-lg">Sign in</Button>
              </Link>
              <Link to="/diagnostics">
                <Button className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] text-xs font-semibold rounded-lg">Free scan</Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
